/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import {BaseFields, BaseModel, BaseProps, createFieldDef, FieldDefinition, ModelFieldType} from './baseModel';
import {
    Country, CustomerType, Fulfilment, InvoiceFilter, InvoiceRequestStatus, InvoiceStatus, InvoiceType, OrderStatus,
    SplitVATValue, VATType
} from '../util/interfaces';
import {ModelResponse, nullify, ResponseData, roundToNumber} from '@dvrd/dvr-controls';
import IDate from '@dvrd/idate';
import Account from './accountModel';
import OrderItem from './orderItemModel';
import {
    assignInvoiceNumber, cancelInvoiceEmails, downloadInvoice, getInvoice, getMinimalInvoices, getPeppolInvoices,
    sendInvoice, sendInvoiceConfigured, sendPeppolInvoice, updateInvoice, UpdateInvoice, uploadInvoice
} from '../util/requestSender';
import History from './historyModel';
import ApiKey from './apiKeyModel';
import {getFromLocal} from '../util/storageUtil';
import {DefaultNumberObject} from '../util/defaultObject';
import {transformCountryName} from '../util/countryUtil';
import {deepClone} from '../util/utils';
import {PeppolState} from '../util/enums';

interface Props extends BaseProps {
    date?: string;
    send_date?: string;
    reference?: string;
    company?: string;
    firstname?: string;
    lastname?: string;
    // Billing details
    street?: string;
    house_number?: string;
    house_number_suffix?: string;
    zip_code?: string;
    city?: string;
    country?: Country;
    // Shipping details
    delivery_street?: string;
    delivery_house_number?: string;
    delivery_house_number_suffix?: string;
    delivery_zip_code?: string;
    delivery_city?: string;
    delivery_country?: Country;
    language?: string;
    email?: string;
    vat_number?: string;
    coc_number?: string;
    error?: string;
    status?: InvoiceStatus;
    total?: number;
    invoice_num?: string;
    customer_type?: CustomerType;
    order_id?: string;
    is_sent?: boolean;
    should_send?: boolean;
    account_id?: string;
    api_key_id?: string;
    account?: Account | any;
    api_key?: ApiKey | any;
    items?: (OrderItem | any)[];
    history?: (History | any)[];
    reference_invoice_id?: string | null;
    reference_invoice?: Invoice | any | null;
    credit_invoice?: Invoice | any | null;
    invoice_type?: InvoiceType;
    will_send_at?: string;
    order_status?: OrderStatus;
    has_reversed_charge?: boolean;
    flex?: Record<string, any>;
    credit_invoice_id?: string;
    fulfilment?: Fulfilment;
    upload_status?: InvoiceRequestStatus | null;
    // Peppol
    sent_peppol?: string | null;
    peppol_state?: PeppolState | null;
    peppol_error?: string | null;
    // History expansion
    sent?: boolean;
    downloaded?: boolean;
    edited?: boolean;
    sent_review?: boolean;
}

export interface InvoiceFields extends BaseFields {
    date: string;
    send_date: string;
    reference: string;
    company: string;
    firstname: string;
    lastname: string;
    // Billing details
    street: string;
    house_number: string;
    house_number_suffix: string;
    zip_code: string;
    city: string;
    country: Country;
    // Shipping details
    delivery_street: string;
    delivery_house_number: string;
    delivery_house_number_suffix: string;
    delivery_zip_code: string;
    delivery_city: string;
    delivery_country: Country;
    language: string;
    email: string;
    vat_number: string;
    coc_number: string;
    error: string;
    status: InvoiceStatus;
    total: number;
    invoice_num: string;
    customer_type: CustomerType;
    order_id: string;
    api_key_id: string;
    api_key: ApiKey | null;
    account_id: string;
    is_sent: boolean;
    should_send: boolean;
    reference_invoice_id: string | null;
    account: Account | null;
    items: OrderItem[];
    history: History[];
    reference_invoice: Invoice | null;
    credit_invoice: Invoice | null;
    invoice_type: InvoiceType;
    will_send_at: string;
    order_status: OrderStatus;
    has_reversed_charge: boolean;
    flex: Record<string, any>;
    credit_invoice_id: string;
    fulfilment: Fulfilment;
    upload_status: InvoiceRequestStatus | null;
    // Peppol
    sent_peppol: string | null;
    peppol_state: PeppolState | null;
    peppol_error: string | null;
    // History expansion
    sent: boolean;
    downloaded: boolean;
    edited: boolean;
    sent_review: boolean;
}

const fields: InvoiceFields = {
    id: '',
    created_at: '',
    updated_at: '',
    date: '',
    send_date: '',
    reference: '',
    company: '',
    firstname: '',
    lastname: '',
    // Billing details
    street: '',
    house_number: '',
    house_number_suffix: '',
    zip_code: '',
    city: '',
    country: Country.NL,
    // Shipping details
    delivery_city: '',
    delivery_country: Country.NL,
    delivery_house_number: '',
    delivery_house_number_suffix: '',
    delivery_street: '',
    delivery_zip_code: '',
    language: 'NL',
    email: '',
    vat_number: '',
    coc_number: '',
    error: '',
    status: InvoiceStatus.IMPORTED,
    total: 0,
    invoice_num: '',
    api_key_id: '',
    api_key: null,
    customer_type: CustomerType.BUSINESS,
    account_id: '',
    order_id: '',
    is_sent: false,
    should_send: false,
    reference_invoice_id: null,
    account: null,
    items: new Array<OrderItem>(),
    history: new Array<History>(),
    reference_invoice: null,
    credit_invoice: null,
    invoice_type: InvoiceType.DEBET,
    will_send_at: '',
    has_reversed_charge: false,
    credit_invoice_id: '',
    upload_status: null,
    // History expansion
    downloaded: false,
    edited: false,
    sent: false,
    sent_review: false,
    order_status: OrderStatus.PLACED,
    flex: {},
    fulfilment: Fulfilment.BOTH,
    sent_peppol: null,
    peppol_state: null,
    peppol_error: null
};

const fieldsDefinition: { [key in keyof Omit<InvoiceFields, 'account' | 'items' | 'history' | 'reference_invoice' | 'credit_invoice' | 'api_key'>]: FieldDefinition } = {
    id: createFieldDef(ModelFieldType.STRING),
    created_at: createFieldDef(ModelFieldType.STRING),
    updated_at: createFieldDef(ModelFieldType.STRING, undefined, true),
    date: createFieldDef(ModelFieldType.STRING),
    send_date: createFieldDef(ModelFieldType.STRING),
    reference: createFieldDef(ModelFieldType.STRING),
    company: createFieldDef(ModelFieldType.STRING),
    firstname: createFieldDef(ModelFieldType.STRING),
    lastname: createFieldDef(ModelFieldType.STRING),
    // Billing details
    street: createFieldDef(ModelFieldType.STRING),
    house_number: createFieldDef(ModelFieldType.STRING),
    house_number_suffix: createFieldDef(ModelFieldType.STRING),
    zip_code: createFieldDef(ModelFieldType.STRING),
    city: createFieldDef(ModelFieldType.STRING),
    country: createFieldDef(ModelFieldType.STRING, Country.NL),
    // Shipping details
    delivery_street: createFieldDef(ModelFieldType.STRING),
    delivery_house_number: createFieldDef(ModelFieldType.STRING),
    delivery_house_number_suffix: createFieldDef(ModelFieldType.STRING),
    delivery_zip_code: createFieldDef(ModelFieldType.STRING),
    delivery_city: createFieldDef(ModelFieldType.STRING),
    delivery_country: createFieldDef(ModelFieldType.STRING, Country.NL),
    email: createFieldDef(ModelFieldType.STRING),
    vat_number: createFieldDef(ModelFieldType.STRING),
    coc_number: createFieldDef(ModelFieldType.STRING),
    error: createFieldDef(ModelFieldType.STRING),
    account_id: createFieldDef(ModelFieldType.STRING),
    status: createFieldDef(ModelFieldType.STRING),
    total: createFieldDef(ModelFieldType.NUMBER),
    invoice_num: createFieldDef(ModelFieldType.STRING),
    customer_type: createFieldDef(ModelFieldType.STRING),
    language: createFieldDef(ModelFieldType.STRING, 'NL'),
    order_id: createFieldDef(ModelFieldType.STRING),
    is_sent: createFieldDef(ModelFieldType.BOOLEAN),
    should_send: createFieldDef(ModelFieldType.BOOLEAN),
    sent_review: createFieldDef(ModelFieldType.BOOLEAN),
    sent: createFieldDef(ModelFieldType.BOOLEAN),
    edited: createFieldDef(ModelFieldType.BOOLEAN),
    downloaded: createFieldDef(ModelFieldType.BOOLEAN),
    api_key_id: createFieldDef(ModelFieldType.STRING),
    reference_invoice_id: createFieldDef(ModelFieldType.STRING, null, true),
    invoice_type: createFieldDef(ModelFieldType.STRING, InvoiceType.DEBET),
    will_send_at: createFieldDef(ModelFieldType.STRING),
    order_status: createFieldDef(ModelFieldType.STRING, OrderStatus.PLACED),
    has_reversed_charge: createFieldDef(ModelFieldType.BOOLEAN),
    flex: createFieldDef(ModelFieldType.OBJECT),
    credit_invoice_id: createFieldDef(ModelFieldType.STRING),
    fulfilment: createFieldDef(ModelFieldType.STRING, Fulfilment.BOTH),
    upload_status: createFieldDef(ModelFieldType.STRING, null, true),
    sent_peppol: createFieldDef(ModelFieldType.STRING, null, true),
    peppol_state: createFieldDef(ModelFieldType.STRING, null, true),
    peppol_error: createFieldDef(ModelFieldType.STRING, null, true)
};

const ignoreProps: Array<keyof Props> = ['account', 'api_key', 'reference_invoice', 'credit_invoice'];

export default class Invoice extends BaseModel<Props, InvoiceFields> {
    static get = (invoice_id: string, accountID: string, callback: ModelResponse<Invoice | null>) => {
        getInvoice({
            invoice_id, account_id: accountID, callback: (data: ResponseData) => {
                const invoice: Invoice | null = data.success ? new Invoice(data.invoice) : null;
                callback(invoice, data.success, data);
            }
        });
    };

    static getAll = (account_id: string, filters: InvoiceFilter, limit: number = 10, page: number = -1,
                     callback: ModelResponse<Invoice[]>, onAbort?: VoidFunction) => {
        return getMinimalInvoices({
            data: {filters, limit, page, account_id}, callback: (data: ResponseData) => {
                const {success} = data,
                    invoices: Invoice[] = success ? data.invoices.map((inv: any) => new Invoice(inv)) : [];
                callback(invoices, success, data);
            }, onAbort
        });
    };

    static getPeppol = (params: {
        accountID: string; peppolState: PeppolState | null; search?: string | null; page: number; limit: number
    }, callback: ModelResponse<Array<Invoice>>) => {
        return getPeppolInvoices({
            data: {
                account_id: params.accountID, peppol_state: params.peppolState, limit: params.limit, page: params.page,
                search: nullify(params.search)
            }, callback: (data: ResponseData) => {
                const invoices: Array<Invoice> = data.success ? data.invoices.map((inv: any) => new Invoice(inv)) : [];
                callback(invoices, data.success, data);
            }
        });
    };

    constructor(props?: Props) {
        super(ignoreProps, fieldsDefinition, fields, props);
        this.build(props);
    }

    get id(): string {
        return this.fields.id as string;
    }

    get date(): string {
        return this.fields.date;
    }

    get momentDate(): IDate {
        return new IDate(this.fields.date);
    }

    set date(date: string) {
        this.fields.date = date;
    }

    get sendDate(): string {
        return this.fields.send_date;
    }

    get momentSendDate(): IDate {
        return new IDate(this.fields.send_date);
    }

    set sendDate(date: string) {
        this.fields.send_date = date;
    }

    get invoiceDate(): IDate {
        if (this.fields.send_date) return this.momentSendDate;
        return this.momentDate;
    }

    get reference(): string {
        return this.fields.reference;
    }

    set reference(reference: string) {
        this.fields.reference = reference;
    }

    get firstname(): string {
        return this.fields.firstname;
    }

    set firstname(firstname: string) {
        this.fields.firstname = firstname;
    }

    get lastname(): string {
        return this.fields.lastname;
    }

    set lastname(lastname: string) {
        this.fields.lastname = lastname;
    }

    get street(): string {
        return this.fields.street;
    }

    set street(street: string) {
        this.fields.street = street;
    }

    get houseNumber(): string {
        return this.fields.house_number;
    }

    set houseNumber(house_number: string) {
        this.fields.house_number = house_number;
    }

    get houseNumberSuffix(): string {
        return this.fields.house_number_suffix;
    }

    set houseNumberSuffix(house_number_suffix: string) {
        this.fields.house_number_suffix = house_number_suffix;
    }

    get zipCode(): string {
        return this.fields.zip_code;
    }

    set zipCode(zip_code: string) {
        this.fields.zip_code = zip_code;
    }

    get city(): string {
        return this.fields.city;
    }

    set city(city: string) {
        this.fields.city = city;
    }

    get email(): string {
        return this.fields.email;
    }

    set email(email: string) {
        this.fields.email = email;
    }

    get vatNumber(): string {
        return this.fields.vat_number;
    }

    set vatNumber(vat_number: string) {
        this.fields.vat_number = vat_number;
    }

    get cocNumber(): string {
        return this.fields.coc_number;
    }

    set cocNumber(coc_number: string) {
        this.fields.coc_number = coc_number;
    }

    get error(): string {
        return this.fields.error;
    }

    set error(error: string) {
        this.fields.error = error;
    }

    get status(): InvoiceStatus {
        return this.fields.status;
    }

    set status(status: InvoiceStatus) {
        this.fields.status = status;
    }

    get total(): number {
        return this.fields.total;
    }

    set total(total: number) {
        this.fields.total = total;
    }

    get totalExcl(): number {
        return roundToNumber(this.items.reduce((prev: number, current: OrderItem) => prev + current.totalExcl, 0), 2);
    }

    get totalNotCancelledExcl(): number {
        return roundToNumber(
            this.items.reduce((prev: number, current: OrderItem) => prev + current.getTotalNotCancelledExcl(), 0), 2);
    }

    get accountId(): string {
        return this.fields.account_id;
    }

    set accountId(account_id: string) {
        this.fields.account_id = account_id;
    }

    get account(): Account | null {
        return this.fields.account;
    }

    set account(account: Account | null) {
        this.fields.account = account;
        if (account) this.accountId = account.id;
    }

    get items(): OrderItem[] {
        return this.fields.items;
    }

    set items(items: OrderItem[]) {
        this.fields.items = items;
    }

    get history(): History[] {
        return this.fields.history;
    }

    set history(history: History[]) {
        this.fields.history = history;
    }

    get fullName(): string {
        return this.firstname + (this.lastname.length ? ` ${this.lastname}` : '');
    }

    get invoiceName(): string {
        return this.company.length ? this.company : this.fullName;
    }

    get translatedStatus(): string {
        if (this.uploadStatus === InvoiceRequestStatus.INVOICE_UPLOADED) return 'Factuur geüpload';
        if (this.orderStatus === OrderStatus.PLACED) return 'Bestelling geplaatst';
        else if (this.orderStatus === OrderStatus.CANCELED) return 'Bestelling geannuleerd';
        switch (this.status) {
            case InvoiceStatus.ERROR:
                return 'Fout op factuur';
            case InvoiceStatus.IMPORTED:
                if (!!this.willSendAt) return 'Factuur nog niet verzonden';
                return 'Factuur niet verzonden';
            case InvoiceStatus.SENT:
                return 'Factuur verzonden';
            case InvoiceStatus.WARNING:
                return 'Waarschuwing';
            case InvoiceStatus.TRANSPORT:
                return 'Factuur wordt verstuurd';
        }
    }

    get invoiceNum(): string {
        return this.fields.invoice_num;
    }

    set invoiceNum(num: string) {
        this.fields.invoice_num = num;
    }

    get customerType(): CustomerType {
        return this.fields.customer_type;
    }

    set customerType(type: CustomerType) {
        this.fields.customer_type = type;
    }

    get translatedCustomerType(): string {
        switch (this.customerType) {
            case CustomerType.BUSINESS:
                return 'Zakelijk';
            case CustomerType.PRIVATE:
                return 'Particulier';
            case CustomerType.BOTH:
                return 'Zakelijk en Particulier';
        }
    }

    get country(): Country {
        return this.fields.country;
    }

    set country(country: Country) {
        this.fields.country = country;
    }

    get countryName(): string {
        if (!this.country.length) return '';
        return transformCountryName(this.country);
    }

    get language(): string {
        return this.fields.language;
    }

    set language(language: string) {
        this.fields.language = language;
    }

    get orderId(): string {
        return this.fields.order_id;
    }

    set orderId(id: string) {
        this.fields.order_id = id;
    }

    get isSent(): boolean {
        return this.fields.is_sent;
    }

    set isSent(is_sent: boolean) {
        this.fields.is_sent = is_sent;
    }

    get company(): string {
        return this.fields.company;
    }

    set company(company: string) {
        this.fields.company = company;
    }

    get sent(): boolean {
        return this.fields.sent;
    }

    get downloaded(): boolean {
        return this.fields.downloaded;
    }

    get edited(): boolean {
        return this.fields.edited;
    }

    get sentReview(): boolean {
        return this.fields.sent_review;
    }

    get shouldSend(): boolean {
        const sendHistoryFrom = getFromLocal('send_history_from');
        if (!sendHistoryFrom)
            return this.fields.should_send;
        if (sendHistoryFrom.includes('::')) {
            const [accountId, sendFrom] = sendHistoryFrom.split('::');
            if (accountId !== this.accountId) return false;
            return this.momentDate.isAtOrAfter(new IDate(sendFrom));
        }
        return this.momentDate.isAtOrAfter(new IDate(sendHistoryFrom));
    }

    set shouldSend(should: boolean) {
        this.fields.should_send = should;
    }

    get apiKeyId(): string {
        return this.fields.api_key_id;
    }

    set apiKeyId(apiKeyId: string) {
        this.fields.api_key_id = apiKeyId;
    }

    get apiKey(): ApiKey | null {
        return this.fields.api_key;
    }

    set apiKey(apiKey: ApiKey | null) {
        this.fields.api_key = apiKey;
        if (apiKey) this.fields.api_key_id = apiKey.id;
    }

    get referenceInvoiceId(): string | null {
        return this.fields.reference_invoice_id;
    }

    set referenceInvoiceId(id: string | null) {
        this.fields.reference_invoice_id = id;
    }

    get referenceInvoice(): Invoice | null {
        return this.fields.reference_invoice;
    }

    set referenceInvoice(invoice: Invoice | null) {
        this.fields.reference_invoice = invoice;
        if (invoice) this.fields.reference_invoice_id = invoice.id;
    }

    get creditInvoice(): Invoice | null {
        return this.fields.credit_invoice;
    }

    set creditInvoice(invoice: Invoice | null) {
        this.fields.credit_invoice = invoice;
    }

    get invoiceType(): InvoiceType {
        return this.fields.invoice_type;
    }

    set invoiceType(invoiceType: InvoiceType) {
        this.fields.invoice_type = invoiceType;
    }

    get notCancelledVatAmount(): Record<string, number> {
        const amounts = new DefaultNumberObject();
        for (const item of this.items) {
            const {vat} = item;
            if ([VATType.MARGIN, VATType.REVERSED, VATType.KOR].includes(vat.vat_type)) continue;
            const quantity = item.quantityNotCancelled;
            if (vat.vat_type === VATType.SPLIT) {
                for (const [percentage, value] of Object.entries((vat as SplitVATValue).value)) {
                    const vatLabel = `${percentage}%`;
                    let vatValue = value * quantity;
                    if (this.invoiceType === InvoiceType.CREDIT) vatValue *= -1;
                    amounts.add(vatLabel, vatValue);
                }
            } else {
                // Default VAT
                const vatLabel = `${vat.value}%`;
                amounts.add(vatLabel, item.getNotCancelledTotalVAT());
            }
        }
        return amounts.toRecord();
    }

    get willSendAt(): string {
        return this.fields.will_send_at;
    }

    set willSendAt(willSendAt: string) {
        this.fields.will_send_at = willSendAt;
    }

    get willSendAtMoment(): IDate | null {
        if (!this.willSendAt) return null;
        return new IDate(this.willSendAt);
    }

    get orderStatus(): OrderStatus {
        return this.fields.order_status;
    }

    set orderStatus(status: OrderStatus) {
        this.fields.order_status = status;
    }

    get hasReversedCharge(): boolean {
        return this.fields.has_reversed_charge;
    }

    set hasReversedCharge(reverseCharge: boolean) {
        this.fields.has_reversed_charge = reverseCharge;
    }

    get flex(): Record<string, any> {
        return this.fields.flex;
    }

    set flex(flex: Record<string, any>) {
        this.fields.flex = flex;
    }

    get deliveryStreet(): string {
        return this.fields.delivery_street;
    }

    set deliveryStreet(street: string) {
        this.fields.delivery_street = street;
    }

    get deliveryHouseNumber(): string {
        return this.fields.delivery_house_number;
    }

    set deliveryHouseNumber(house_number: string) {
        this.fields.delivery_house_number = house_number;
    }

    get deliveryHouseNumberSuffix(): string {
        return this.fields.delivery_house_number_suffix;
    }

    set deliveryHouseNumberSuffix(house_number_suffix: string) {
        this.fields.delivery_house_number_suffix = house_number_suffix;
    }

    get deliveryZipCode(): string {
        return this.fields.delivery_zip_code;
    }

    set deliveryZipCode(zip_code: string) {
        this.fields.delivery_zip_code = zip_code;
    }

    get deliveryCity(): string {
        return this.fields.delivery_city;
    }

    set deliveryCity(city: string) {
        this.fields.delivery_city = city;
    }

    get deliveryCountry(): Country {
        return this.fields.delivery_country;
    }

    set deliveryCountry(country: Country) {
        this.fields.delivery_country = country;
    }

    get creditInvoiceId(): string {
        return this.fields.credit_invoice_id;
    }

    get hasCreditInvoice(): boolean {
        return this.creditInvoiceId.length > 0;
    }

    get fulfilment(): Fulfilment {
        return this.fields.fulfilment;
    }

    set fulfilment(fulfilment: Fulfilment) {
        this.fields.fulfilment = fulfilment;
    }

    get uploadStatus(): InvoiceRequestStatus | null {
        return this.fields.upload_status;
    }

    set uploadStatus(uploadStatus: InvoiceRequestStatus | null) {
        this.fields.upload_status = uploadStatus;
    }

    get sentPeppol(): IDate | null {
        if (!this.fields.sent_peppol) return null;
        return new IDate(this.fields.sent_peppol);
    }

    get peppolState(): PeppolState | null {
        return this.fields.peppol_state;
    }

    get peppolError(): string | null {
        return this.fields.peppol_error;
    }

    peppolApplies = (): boolean => {
        return this.countryName === 'België' && this.vatNumber.startsWith('BE');
    };

    clone = (deep: boolean = true): Invoice => {
        let fields = this.fields;
        if (deep) fields = deepClone(fields);
        return new Invoice(fields);
    };

    getFlex = (key: string): any | null => {
        return this.flex[key];
    };

    setFlex = (key: string, value: any) => {
        const {flex} = this;
        flex[key] = value;
        this.flex = flex;
    };

    addItem = (item: OrderItem): Invoice => {
        for (const itm of this.items)
            if (itm.id === item.id) return this.updateItem(item);
        this.items = this.items.concat(item);
        return this;
    };

    updateItem = (item: OrderItem): Invoice => {
        const items = this.items;
        for (let i = 0; i < items.length; i++) {
            if (items[i].id === item.id) {
                items.splice(i, 1, item);
                break;
            }
        }
        this.items = items;
        return this;
    };

    removeItem = (item: OrderItem): Invoice => {
        this.items = this.items.filter((itm: OrderItem) => itm.id !== item.id);
        return this;
    };

    addHistory = (history: History): Invoice => {
        this.history = this.history.concat(history);
        return this;
    };

    updateHistory = (history: History): Invoice => this.removeHistory(history).addHistory(history);

    removeHistory = (history: History): Invoice => {
        this.history = this.history.filter((hst: History) => hst.id !== history.id);
        return this;
    };

    update = (callback: ModelResponse<Invoice>) => {
        const data: UpdateInvoice = {
            invoice_id: this.id,
            account_id: this.accountId,
            city: this.city,
            coc_number: nullify(this.cocNumber),
            date: this.date,
            email: this.email,
            company: this.company,
            firstname: this.firstname,
            house_number: this.houseNumber,
            house_number_suffix: nullify(this.houseNumberSuffix),
            items: this.items.map((item: OrderItem) => ({
                item_id: item.itemId,
                ean: item.ean,
                title: item.title,
                quantity: item.quantity,
                price: item.piecePrice * item.quantity,
                reference: item.reference,
                vat: item.vat
            })),
            lastname: this.lastname,
            reference: nullify(this.reference),
            street: this.street,
            vat_number: this.vatNumber,
            zip_code: this.zipCode,
            country: this.country
        };
        updateInvoice({
            data, callback: (data: ResponseData) => {
                if (data.success) this.build(data.invoice);
                callback(this, data.success, data);
            }
        });
    };

    assignInvoiceNum = (prefix: string, invoiceNum: string, callback: ModelResponse<Invoice>) => {
        assignInvoiceNumber({
            data: {
                invoice_id: this.id,
                account_id: this.accountId,
                invoice_num: invoiceNum,
                prefix: nullify(prefix)
            }, callback: (data: ResponseData) => {
                if (data.success) this.build(data.invoice);
                callback(this, data.success, data);
            }
        });
    };

    cancelEmails = (callback: ModelResponse<Invoice>) => {
        cancelInvoiceEmails({
            invoice_id: this.id, account_id: this.accountId, callback: (data: ResponseData) => {
                if (data.success) this.build(data.invoice);
                callback(this, data.success, data);
            }
        });
    };

    send = (callback: ModelResponse<Invoice>) => {
        sendInvoice({
            invoice_id: this.id, account_id: this.accountId, callback: (data: ResponseData) => {
                if (data.success) this.build(data.invoice);
                callback(this, data.success, data);
            }
        });
    };

    sendConfigured = (mailText: string, mailJson: Record<string, any>, subject: string,
                      callback: ModelResponse<Invoice>) => {
        sendInvoiceConfigured({
            data: {
                invoice_id: this.id, account_id: this.accountId, subject, mail_text: mailText, mail_json: mailJson
            }, callback: (data: ResponseData) => {
                if (data.success) this.build(data.invoice);
                callback(this, data.success, data);
            }
        });
    };

    createCredit = (): Invoice => {
        const copy = this.clone();
        copy.invoiceType = InvoiceType.CREDIT;
        return copy;
    };

    download = (callback: (invoice: Invoice, blob: Blob | null, success: boolean, data: ResponseData,
                           response: Response) => void) => {
        downloadInvoice({
            invoice_id: this.id, account_id: this.accountId, callback: (data: ResponseData, response: Response) => {
                if (data.status === 'success') {
                    // FILE RESPONSE
                    response.blob().then(blob => {
                        callback(this, blob, true, data, response);
                    });
                } else
                    callback(this, null, false, data, response);
            }
        });
    };

    upload = (callback: ModelResponse<Invoice>) => {
        uploadInvoice({
            data: {invoice_id: this.id, account_id: this.accountId}, callback: (data: ResponseData) => {
                callback(this, data.success, data);
            }
        });
    };

    getNumProducts = (): number => {
        return this.items.reduce((prev: number, current: OrderItem) => prev + current.quantityShipped, 0);
    };

    sendPeppol = (callback: ModelResponse<Invoice>) => {
        return sendPeppolInvoice({
            data: {invoice_id: this.id, account_id: this.accountId},
            callback: (data: ResponseData) => {
                if (data.success) this.build(data.invoice);
                callback(this, data.success, data);
            }
        });
    };

    protected build = (props?: Props) => {
        this.constructFields(props);
        if (props) {
            if (props.account) {
                const account: Account = props.account instanceof Account ? props.account : new Account(props.account);
                account.addInvoice(this);
                this.account = account;
            }
            if (props.items) {
                const items: OrderItem[] = [];
                for (const itm of props.items) {
                    const item = itm instanceof OrderItem ? itm : new OrderItem(itm);
                    item.invoice = this;
                    items.push(item);
                }
                this.fields.items = items;
            }
            if (props.history) {
                const histories: History[] = [];
                for (const hst of props.history) {
                    const history: History = hst instanceof History ? hst : new History(hst);
                    history.invoice = this;
                    histories.push(history);
                }
                this.fields.history = histories;
            }
            if (props.api_key)
                this.apiKey = props.api_key instanceof ApiKey ? props.api_key : new ApiKey(props.api_key);
            if (props.reference_invoice) {
                const refInvoice: Invoice = props.reference_invoice instanceof Invoice ? props.reference_invoice :
                    new Invoice(props.reference_invoice);
                refInvoice.creditInvoice = this;
                this.referenceInvoice = refInvoice;
            }
            if (props.credit_invoice) {
                const credInvoice: Invoice = props.credit_invoice instanceof Invoice ? props.credit_invoice :
                    new Invoice(props.credit_invoice);
                credInvoice.referenceInvoice = this;
                this.creditInvoice = credInvoice;
            }
        }
    };
}